import type { EnnismoreTheme } from '@/ui/theme';

const fonts = {
  headline: 'nimbusSans, sans-serif',
  intro: 'nimbusSans, sans-serif',
  subheader: 'nimbusSans, sans-serif',
  body: 'nimbusSans, sans-serif',
};

export const hydeTheme: EnnismoreTheme = {
  themeKey: 'hyde',
  logoUrl: '/brands/hyde/images/logo.svg',
  faviconUrl: '/brands/hyde/favicons/favicon.webp',
  coBrandedDisloyaltyLogoUrl: '/disloyalty/brands/hyde.svg',
  space: ['0px', '8px', '16px', '20px', '24px', '32px', '40px', '56px'],
  fonts,
  text: {
    heading1: {
      fontFamily: fonts.headline,
      fontSize: '32px',
      lineHeight: '34px',
      textTransform: 'uppercase',
      letterSpacing: '8px',
    },
    heading2: {
      fontFamily: fonts.headline,
      fontSize: '56px',
      lineHeight: '64px',
    },
    heading3: {
      fontFamily: fonts.headline,
      fontSize: '40px',
      lineHeight: '48px',
    },
    heading4: {
      fontFamily: fonts.headline,
      fontSize: '32px',
      lineHeight: '40px',
    },
    heading5: {
      fontFamily: fonts.headline,
      fontSize: '26px',
      lineHeight: '34px',
    },
    heading6: {
      fontFamily: fonts.headline,
      fontSize: '24px',
      lineHeight: '34px',
    },
    intro1: {
      fontFamily: fonts.intro,
      fontSize: '40px',
      lineHeight: '56px',
    },
    intro2: {
      fontFamily: fonts.intro,
      fontSize: '24px',
      lineHeight: '40px',
    },
    subheader1: {
      fontFamily: fonts.subheader,
      fontSize: '24px',
      lineHeight: '32px',
    },
    subheader2: {
      fontFamily: fonts.subheader,
      fontSize: '18px',
      lineHeight: '32px',
    },
    subheader3: {
      fontFamily: fonts.body,
      fontSize: '16px',
      lineHeight: '24px',
      textTransform: 'uppercase',
    },
    bodyLarge: {
      fontFamily: fonts.body,
      fontSize: '20px',
      lineHeight: '32px',
    },
    bodyCopy: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '20px',
    },
    bodySmall: {
      fontFamily: fonts.body,
      fontSize: '14px',
      lineHeight: '20px',
    },
    bodyExtraSmall: {
      fontFamily: fonts.body,
      fontSize: '11px',
      lineHeight: '22px',
    },
    captions: {
      fontFamily: fonts.body,
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  colors: {
    white: '#fff',
    textPrimary: '#381C29',
    textSecondary: '#6A6A6A',
    background: '#F3E7D7',
    primary100: '#FEF9F3',
    primary200: '#FEF2E3',
    primary300: '#264540',
    primary400: '#FFE7D9',
    primary500: '#FF9272',
    secondary100: '#F6F6F6',
    secondary200: '#D9D9D9',
    secondary300: '#626865',
    secondary400: '#3B423F',
    error: '#B00020',
    sale: '#B00020',
  },
  buttons: {
    primary: {
      fontFamily: fonts.body,
      fontWeight: 400,
      backgroundColor: '#381C29',
      color: '#F3E7D7',
      borderRadius: '32px',
      transition: 'background-color .3s ease-in, color .3s ease-in',
      letterSpacing: '0.98px',
      ':hover': {
        backgroundColor: '#FFB8E3',
        color: '#381C29',
      },
    },
    secondary: {
      fontFamily: fonts.body,
      fontWeight: 400,
      backgroundColor: 'transparent',
      borderRadius: '100px',
      letterSpacing: '0.98px',
      color: '#381C29',
      border: '1px solid #381C29',
    },
  },
  forms: {
    label: {
      fontSize: '16px',
    },
    input: {
      backgroundColor: 'white',
      color: '#381C29',
      fontSize: '16px',
      border: '1px solid #381C29',
      borderRadius: '0px',
    },
    radioButton: {
      selected: {
        backgroundColor: 'white',
      },
      description: {
        color: '#381C29',
      },
    },
    stripe: {
      tab: {
        backgroundColor: 'white',
      },
    },
    checkoutForm: {
      billingInformationDescription: {
        opacity: 1,
      },
    },
  },
  fontFaceDefinitions: [
    {
      fontFamily: 'nimbusSans',
      fontWeight: 400,
      sources: {
        woff2:
          'https://use.typekit.net/af/da0c19/00000000000000000001518d/27/l?primer=7fa3915bdafdf03041871920a205bef951d72bf64dd4c4460fb992e3ecc3a862&fvd=n4&v=3',
      },
    },
  ],
  keylines: {
    border: '1px solid #C0BAB2',
  },
  componentProperties: {
    search: {
      results: {
        sidebarContainer: {
          hotelPerks: {
            border: 0,
          },
        },
      },
      collapsibleForm: {
        searchSummary: {
          fontFamily: fonts.body,
          expanded: {
            borderRadius: '6px 6px 0 0',
          },
          modifySearchButton: {
            textTransform: 'uppercase',
          },
        },
        searchForm: {
          borderRadius: '0px 0px 6px 6px',
        },
      },
    },
    header: {
      backgroundColor: '#F3E7D7',
      borderBottom: '1px solid #381C29',
      logo: {
        borderRight: 'none',
        left: '65px',
      },
      height: 80, // default 60px + 10px 0 padding
      wizard: {
        step: {
          fontSize: '14px',
          letterSpacing: '1.96px',
          lineHeight: '18px',
          textTransform: 'uppercase',
          active: {
            fontWeight: 600,
          },
        },
      },
    },
    sidebarCards: {
      primary: {
        borderRadius: '0',
        backgroundColor: 'white',
        borderTop: 'none',
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none',
      },
      secondary: {
        borderRadius: '6px',
        backgroundColor: 'transparent',
        borderColor: 'primary300',
        borderTop: '1px solid #381C29',
        borderBottom: '1px solid #381C29',
        borderLeft: '1px solid #381C29',
        borderRight: '1px solid #381C29',
      },
    },
    cards: {
      primary: {
        borderRadius: '6px',
        mobile: {
          border: 'none',
        },
      },
    },
    bedroomCard: {
      roomTitle: {
        color: '#381C29',
      },
      roomRateName: {
        color: '#381C29',
        textTransform: 'uppercase',
        fontFamily: fonts.body,
        fontSize: '16px',
      },
      roomSpecItem: {
        color: '#989898',
        value: {
          color: '#381C29',
        },
      },
    },
    halfScreenLayout: {
      borderRight: 'none',
    },
    checkoutSidebar: {
      border: 'none',
      cancellationPolicy: {
        title: {
          fontFamily: fonts.body,
          fontSize: '18px',
          lineHeight: '30px',
        },
        description: {
          color: '#381C29',
        },
      },
    },
    staySummary: {
      border: 'none',
      color: '#381C29',
      borderRadius: '12px',
      dataRow: {
        label: {
          opacity: 1,
          fontSize: '14px',
        },
        value: {
          fontSize: '14px',
          fontWeight: 600,
        },
      },
      title: {
        padding: '18px 0 18px',
        fontSize: '24px',
        mobile: {
          fontSize: '18px',
        },
      },
      dropdown: {
        textTransform: 'uppercase',
      },
    },
    footer: {
      backgroundColor: '#381C29',
      color: '#FFB8E3',
    },
    calendar: {
      color: '#381C29',
      header: {
        backgroundColor: '#fff',
        borderBottom: '1px solid #381C29',
      },
      body: {
        date: {
          selected: {
            backgroundColor: '#381C29',
          },
          selectedMiddle: {
            backgroundColor: '#C7B65C',
          },
        },
      },
    },
    page: {
      background: '#F3E7D7',
    },
    confirmation: {
      mobile: {
        rowGap: '20px',
      },
      title: {
        fontSize: '32px',
        textTransform: 'uppercase',
        letterSpacing: '8px',
        mobile: {
          fontSize: '26px',
        },
      },
      confirmationMessage: {
        fontSize: '16px',
      },
      header: {
        paddingBottom: '32px',
        mobile: {
          paddingBottom: '0px',
        },
        backgroundColor: '#C7B65C',
      },
      headerImage: {
        height: '480px',
        objectFit: 'cover',
        container: {
          margin: '0',
          width: '100%',
          paddingBottom: '48px',
        },
      },
      anyQuestionsCard: {
        title: {
          fontSize: '24px',
          mobile: {
            fontSize: '20px',
          },
        },
      },
      restaurantsCard: {
        title: {
          fontSize: '24px',
          mobile: {
            fontSize: '20px',
          },
        },
        restaurantTitle: {
          fontSize: '18px',
        },
      },
      whereToFindUsCard: {
        title: {
          fontSize: '24px',
          mobile: {
            fontSize: '20px',
          },
        },
      },
      bookingConfirmationAdditionalDetailsContainer: {
        borderRadius: '12px',
      },
      bookingConfirmationDetailsCard: {
        customerNameHeading: {
          fontSize: '18px',
          fontWeight: 400,
        },
        bookingNumberHeading: {
          fontSize: '24px',
          fontWeight: 700,
          mobile: {
            fontSize: '20px',
          },
        },
        addressHeading: {
          fontSize: '14px',
          fontWeight: 400,
        },
        manageMyBooking: {
          title: {
            fontSize: '18px',
          },
          link: {
            fontSize: '16px',
            color: '#381C29',
            fontFamily: fonts.body,
          },
        },
        paymentCancellationPolicy: {
          title: {
            fontSize: '18px',
          },
        },
      },
      bookingConfirmationContainer: {
        margin: '60px auto auto auto',
        mobile: {
          margin: '40px 16px 0',
        },
      },
    },
    consentInputs: {
      divider: { borderColor: '#C0BAB2' },
    },
    bookingManagement: {
      title: {
        fontSize: '32px',
        lineHeight: '34px',
        textTransform: 'uppercase',
        letterSpacing: '8px',
        mobile: {
          fontSize: '26px',
        },
      },
      bookingCard: {
        header: {
          cancelled: {
            backgroundColor: '#fff',
            color: '#381C29',
          },
          backgroundColor: '#381C29',
          color: '#fff',
          fontSize: '14px',
          borderBottom: '1px solid #C0BAB2',
        },
        button: {
          variant: 'secondary',
        },
        cancellationModal: {
          borderRadius: 0,
        },
      },
    },
    paymentMethods: {
      googlePay: {
        borderRadius: '32px',
      },
      applePay: {
        borderRadius: '32px',
      },
    },
  },
  icons: {},
  __temporary: {
    displayCarTransportOnNewLine: true,
  },
};
